import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import {
    Plus, Search, Pencil, X, Loader2, AlertTriangle, ShieldAlert, ShieldCheck,
    ShieldX, ScanLine, Wrench, Archive, RotateCcw, MapPin, Cpu, Clock,
    CalendarClock, Radiation, Baby, Info,
} from 'lucide-react';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import CommonLoader from '../../components/CommonLoader';
// A factory, not a hook — imported under its real name so
// react-hooks/rules-of-hooks does not key on the identifier. See usePermissions.js.
import createAuthedAxios from '../../hooks/useAxiosAuth';
import usePermissions from '../../hooks/usePermissions';
import TimeSelect from '../../components/common/TimeSelect';
import { formatClock } from '../../utils/clock';

const MySwal = withReactContent(Swal);

// THE MACHINES — one card per X-ray unit, ultrasound console, CT gantry, MRI
// scanner, mammography unit or echo machine the department owns, in the room it
// stands in, with the licence that says it may be switched on.
//
// A radiology department's capacity IS its machines. A study is booked against a
// machine for a span of minutes, so every one of them has to exist as a row with
// its own working day before a single slot can be computed.
//
// ---------------------------------------------------------------------------
// WHY THE REGISTRATION DATES ARE THE POINT OF THIS SCREEN, AND WHY THEY GO RED
//
// The licences are issued to the MACHINE, not to the hospital.
//
//   AERB registers each radiation-emitting UNIT individually under the Atomic
//   Energy (Radiation Protection) Rules — this tube, this gantry, at this
//   address, with its own number and its own expiry. A hospital with two X-ray
//   rooms routinely has one current and one lapsed: the second-hand mobile unit
//   bought last year that nobody registered, or the CT whose renewal was missed
//   while the physicist was on leave.
//
//   PC-PNDT registers the ULTRASOUND MACHINE capable of foetal imaging, at a
//   named registered address, to a named centre. Move that console to the branch
//   across town and the registration does not travel with it — scanning on it
//   there is an offence on the day it arrives.
//
// Operating an unregistered unit gets the machine sealed and the person in charge
// prosecuted. So an expired date here is not a nag that can be dismissed and read
// next week: services/radiology REFUSES to schedule or perform on that machine,
// by name, while the room next door carries on unaffected. This screen exists so
// that refusal is never the first anybody hears of it — a renewal chased in the
// month before it bites, instead of discovered at 9 a.m. with a patient at the
// counter and a morning list to cancel.
//
// THE COLOURS ARE THEREFORE A CLAIM ABOUT CONSEQUENCE, NOT A MOOD:
//   RED   — this machine cannot lawfully be used for that work today. Expired, or
//           a registration number with no expiry recorded (which the gate reads as
//           unregistered, because that is exactly what an unregistered machine
//           looks like), or an ionising unit with no number at all.
//   AMBER — still lawful, expiring inside the renewal window. This is the only
//           colour anybody should ever have to act on, because it is the only one
//           where there is still time.
//   TEAL  — current, with the date it runs to.
//   GREY  — does not apply. AERB genuinely does not register ultrasound, MRI, echo
//           or ECG, and demanding a number for them would stop a department that
//           has done nothing wrong.
// Every one of them is also stated in WORDS on the card. Colour alone excludes
// roughly one man in twelve, and this is the screen where being excluded means
// missing an expired licence.
//
// A MISSING PC-PNDT NUMBER ON AN ULTRASOUND IS NOT PAINTED RED, and that is
// deliberate rather than an oversight. The Act registers a machine for use in
// pre-natal diagnostic procedures: an abdominal ultrasound on an unregistered
// console is lawful, an obstetric scan on it is not. Painting it red would mark a
// general ultrasound room as broken over paperwork the Act does not require for
// its work — and a screen that cries wolf about lawful work is a screen people
// stop reading, which is how the real expiry gets missed. It is stated as a scope
// limit instead: this console may not be used for foetal imaging.
//
// MACHINES THAT CANNOT BE BOOKED SORT FIRST, whatever the filter says. The
// question this page answers is "what is stopping work today", and an answer
// buried at position eleven in creation order is not an answer.
//
// ---------------------------------------------------------------------------
// MACHINES ARE RETIRED, NEVER DELETED. Years of studies quote equipmentId, and
// the question an AERB or PC-PNDT inspection actually asks is "which unit
// performed this exposure, and was it registered on that date" — unanswerable
// from a row removed when the machine was sold.
//
// RETIRING GOES THROUGH status AND NOT THROUGH A DELETE. status is a three-value
// ENUM and 'maintenance' is a real third state, not a flavour of retired: a
// scanner down for a tube change is out of the schedule this week and back in it
// next week. A DELETE verb can only express one of the three, so the day a
// machine comes back somebody has to remember which of the two it had been.
//
// NO REDUX SLICE, deliberately — the same position the pharmacy master screens
// take (see the note at the top of RackLocations.jsx). Registering one means
// touching src/store.js, which this screen ships ahead of.
//
// PERMISSIONS ARE NOT ENFORCED HERE. requireTenantFeature('radiology') and
// requirePermission('radiology.master.manage') guard every route. The can() check
// below only decides whether a control is drawn and says so in words when it is
// not — a 403 after somebody has typed an AERB certificate number off a piece of
// paper is a wasted trip to the filing cabinet.

const API = {
    list: '/radiology/equipment',
    one: (equipmentId) => `/radiology/equipment/${equipmentId}`,
};

// A department owns machines in the tens, never in the thousands — the whole
// fleet is fetched at once and there is no pager. A compliance list split across
// pages is one where the lapsed unit is on page two.
const FLEET_LIMIT = 200;
const SEARCH_DEBOUNCE_MS = 500;

// How far ahead a renewal starts shouting. Sixty days because that is roughly
// what an AERB or PC-PNDT renewal actually takes to come back once the
// application, the physicist's survey and the inspection are counted — a warning
// that arrives a fortnight before expiry is a warning that arrives too late to
// be acted on, which makes it decoration.
const EXPIRING_SOON_DAYS = 60;

const apiError = (err, fallback) =>
    err?.response?.data?.message || err?.message || fallback;

// Mirrors the `modality` ENUM on Backend/models/imagingEquipmentModel.js — and,
// identically, the one on imagingProcedureModel.js. The two lists MUST stay the
// same: scheduling matches procedure.modality against equipment.modality, so a
// value on one side and not the other is a procedure no machine can be found for,
// and the scheduler answers "no equipment available" for a room standing empty.
//
// `ionising` is IONISING_MODALITIES from services/radiology/imagingService.js:
// the modalities AERB registers individually. `foetal` marks the one modality
// PC-PNDT speaks about at all.
const MODALITIES = {
    xray: { labelKey: 'imagingequipment.modality_xray', label: 'X-ray', chip: 'bg-slate-100 text-slate-700 border-slate-300', ionising: true, foetal: false },
    ultrasound: { labelKey: 'imagingequipment.modality_ultrasound', label: 'Ultrasound', chip: 'bg-sky-50 text-sky-800 border-sky-300', ionising: false, foetal: true },
    ct: { labelKey: 'imagingequipment.modality_ct', label: 'CT', chip: 'bg-amber-50 text-amber-800 border-amber-300', ionising: true, foetal: false },
    mri: { labelKey: 'imagingequipment.modality_mri', label: 'MRI', chip: 'bg-violet-50 text-violet-800 border-violet-300', ionising: false, foetal: false },
    mammography: { labelKey: 'imagingequipment.modality_mammography', label: 'Mammography', chip: 'bg-rose-50 text-rose-800 border-rose-300', ionising: true, foetal: false },
    dexa: { labelKey: 'imagingequipment.modality_dexa', label: 'DEXA', chip: 'bg-orange-50 text-orange-800 border-orange-300', ionising: true, foetal: false },
    echo: { labelKey: 'imagingequipment.modality_echo', label: 'Echo', chip: 'bg-emerald-50 text-emerald-800 border-emerald-300', ionising: false, foetal: false },
    ecg: { labelKey: 'imagingequipment.modality_ecg', label: 'ECG', chip: 'bg-teal-50 text-teal-800 border-teal-300', ionising: false, foetal: false },
    other: { labelKey: 'imagingequipment.modality_other', label: 'Other', chip: 'bg-gray-100 text-gray-600 border-gray-300', ionising: false, foetal: false },
};
const MODALITY_KEYS = Object.keys(MODALITIES);

const STATUSES = {
    active: { labelKey: 'imagingequipment.status_active', label: 'In service', icon: ScanLine, chip: 'bg-[#4ABDBD]/15 text-[#2D8A8A] border-[#4ABDBD]/40' },
    maintenance: { labelKey: 'imagingequipment.status_maintenance', label: 'Under maintenance', icon: Wrench, chip: 'bg-amber-100 text-amber-800 border-amber-300' },
    retired: { labelKey: 'imagingequipment.status_retired', label: 'Retired', icon: Archive, chip: 'bg-slate-200 text-slate-600 border-slate-300' },
};

const EMPTY_EQUIPMENT = {
    equipmentId: null,
    name: '',
    modality: 'xray',
    manufacturer: '',
    model: '',
    serialNo: '',
    room: '',
    aerbRegNo: '',
    aerbExpiry: '',
    pndtRegNo: '',
    pndtExpiry: '',
    status: 'active',
    slotMinutes: 15,
    openTime: '09:00',
    closeTime: '18:00',
};

/* ------------------------------------------------------------------ *
 * Dates
 *
 * A licence is valid to the END of a calendar day in the hospital's own zone,
 * which is why the column is DATEONLY. Everything below therefore compares
 * CALENDAR DAYS and never instants: toISOString() is UTC and hands back
 * yesterday for anybody east of Greenwich after teatime, which on this screen
 * would paint a machine's registration as expired — and, worse, would read as
 * lapsed on the very morning it is still lawful.
 * ------------------------------------------------------------------ */

/** Whole days from today to a 'YYYY-MM-DD'. Negative once it has passed. */
const daysUntil = (dateOnly) => {
    const s = String(dateOnly || '').slice(0, 10);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return null;
    const [y, m, d] = s.split('-').map(Number);
    // Constructed from parts, at local midnight — new Date('2026-03-14') parses
    // as UTC midnight and is a day out for half the world.
    const target = new Date(y, m - 1, d);
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    return Math.round((target - start) / 86400000);
};

const fmtDate = (dateOnly) => {
    const s = String(dateOnly || '').slice(0, 10);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return '—';
    const [y, m, d] = s.split('-').map(Number);
    return new Date(y, m - 1, d).toLocaleDateString('en-GB');
};

/* ------------------------------------------------------------------ *
 * The compliance verdict
 *
 * Mirrors registrationProblem() in services/radiology/imagingService.js — which
 * is the authority, and the only thing that actually refuses a booking. This is
 * the same rule drawn on screen so a department can see what the gate will do
 * BEFORE it does it with a patient present. Where the two could ever disagree,
 * the server wins and this screen is the one to correct.
 * ------------------------------------------------------------------ */

const LEVEL = {
    blocked: {
        tone: 'bg-red-50 text-red-800 border-red-300',
        dot: 'bg-red-600',
        icon: ShieldX,
    },
    expiring: {
        tone: 'bg-amber-50 text-amber-800 border-amber-300',
        dot: 'bg-amber-500',
        icon: ShieldAlert,
    },
    ok: {
        tone: 'bg-[#4ABDBD]/10 text-[#2D8A8A] border-[#4ABDBD]/40',
        dot: 'bg-[#2D8A8A]',
        icon: ShieldCheck,
    },
    scoped: {
        tone: 'bg-slate-50 text-slate-600 border-slate-300 border-dashed',
        dot: 'bg-slate-300',
        icon: Info,
    },
    na: {
        tone: 'bg-slate-50 text-slate-400 border-slate-200',
        dot: 'bg-slate-200',
        icon: Info,
    },
};

/**
 * AERB, for one machine.
 *
 * Applies to the ionising modalities, AND to any machine that carries an AERB
 * number whatever its modality — that second clause is what closes the soft edge
 * a fluoroscopy or C-arm unit entered as 'other' would otherwise slip through.
 * It is the same closure imagingService makes, from the same direction.
 *
 * A MISSING NUMBER ON AN IONISING UNIT IS 'blocked', NOT 'unknown'. An empty
 * field is precisely what an unregistered machine looks like; reading it as "no
 * restriction applies" would make the gate fire on every properly-recorded
 * machine and never on the one it exists to stop.
 */
const aerbState = (eq) => {
    const cfg = MODALITIES[eq.modality] || MODALITIES.other;
    const regNo = String(eq.aerbRegNo || '').trim();
    const applies = cfg.ionising || !!regNo;
    if (!applies) return { level: 'na', key: 'aerb_na' };
    if (!regNo) return { level: 'blocked', key: 'aerb_missing' };
    if (!eq.aerbExpiry) return { level: 'blocked', key: 'aerb_no_expiry', regNo };

    const days = daysUntil(eq.aerbExpiry);
    if (days === null) return { level: 'blocked', key: 'aerb_no_expiry', regNo };
    if (days < 0) return { level: 'blocked', key: 'aerb_expired', regNo, days };
    if (days <= EXPIRING_SOON_DAYS) return { level: 'expiring', key: 'aerb_expiring', regNo, days };
    return { level: 'ok', key: 'aerb_ok', regNo, days };
};

/**
 * PC-PNDT, for one machine.
 *
 * Only ever discussed on ultrasound (and on any machine that carries a number,
 * for the same soft-edge reason as above). A console with no registration is
 * 'scoped' rather than 'blocked' — see the header for why marking lawful general
 * ultrasound as broken is the more dangerous mistake.
 */
const pndtState = (eq) => {
    const cfg = MODALITIES[eq.modality] || MODALITIES.other;
    const regNo = String(eq.pndtRegNo || '').trim();
    const relevant = cfg.foetal || !!regNo;
    if (!relevant) return { level: 'na', key: 'pndt_na' };
    if (!regNo) return { level: 'scoped', key: 'pndt_unregistered' };
    if (!eq.pndtExpiry) return { level: 'blocked', key: 'pndt_no_expiry', regNo };

    const days = daysUntil(eq.pndtExpiry);
    if (days === null) return { level: 'blocked', key: 'pndt_no_expiry', regNo };
    if (days < 0) return { level: 'blocked', key: 'pndt_expired', regNo, days };
    if (days <= EXPIRING_SOON_DAYS) return { level: 'expiring', key: 'pndt_expiring', regNo, days };
    return { level: 'ok', key: 'pndt_ok', regNo, days };
};

/** Retired machines are history, not a compliance problem — nothing is booked on
 *  them, so a lapsed certificate on one is a fact rather than a fault. */
const blocksWork = (eq) =>
    eq.status !== 'retired' && (aerbState(eq).level === 'blocked' || pndtState(eq).level === 'blocked');

/* ------------------------------------------------------------------ *
 * Presentational pieces
 * ------------------------------------------------------------------ */

/**
 * One registration line: what the certificate says, and what it means today.
 *
 * The NUMBER is shown in monospace and in full. It is what an inspector matches
 * against the paper in the file, so it is transcribed, not summarised — a
 * truncated registration number is a number that has to be looked up somewhere
 * else, which is the moment the check stops being done.
 */
function RegistrationRow({ kind, state, regNo, expiry, testId }) {
    const { t } = useTranslation();
    const style = LEVEL[state.level] || LEVEL.na;
    const Icon = style.icon;
    const KindIcon = kind === 'aerb' ? Radiation : Baby;

    const WORDING = {
        aerb_na: t('imagingequipment.aerb_na', 'AERB does not register this modality'),
        aerb_missing: t('imagingequipment.aerb_missing', 'No AERB registration on file — this unit may not be used'),
        aerb_no_expiry: t('imagingequipment.aerb_no_expiry', 'AERB number recorded with no expiry — treated as unregistered'),
        aerb_expired: t('imagingequipment.aerb_expired', 'AERB registration EXPIRED on {{date}}', { date: fmtDate(expiry) }),
        aerb_expiring: t('imagingequipment.aerb_expiring', 'AERB expires in {{n}} day(s), on {{date}}', { n: state.days, date: fmtDate(expiry) }),
        aerb_ok: t('imagingequipment.aerb_ok', 'AERB current to {{date}}', { date: fmtDate(expiry) }),
        pndt_na: t('imagingequipment.pndt_na', 'PC-PNDT does not apply to this machine'),
        pndt_unregistered: t('imagingequipment.pndt_unregistered', 'Not PC-PNDT registered — general scans only, no foetal imaging'),
        pndt_no_expiry: t('imagingequipment.pndt_no_expiry', 'PC-PNDT number recorded with no expiry — treated as unregistered'),
        pndt_expired: t('imagingequipment.pndt_expired', 'PC-PNDT registration EXPIRED on {{date}}', { date: fmtDate(expiry) }),
        pndt_expiring: t('imagingequipment.pndt_expiring', 'PC-PNDT expires in {{n}} day(s), on {{date}}', { n: state.days, date: fmtDate(expiry) }),
        pndt_ok: t('imagingequipment.pndt_ok', 'PC-PNDT current to {{date}}', { date: fmtDate(expiry) }),
    };

    return (
        <div
            data-testid={testId}
            data-level={state.level}
            className={`flex items-start gap-2 px-2.5 py-2 rounded-lg border text-[11px] font-semibold ${style.tone}`}
        >
            <span className="flex items-center gap-1 flex-shrink-0 pt-0.5">
                <span className={`w-2 h-2 rounded-full ${style.dot}`} />
                <KindIcon className="w-3.5 h-3.5" />
            </span>
            <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5">
                    <Icon className="w-3.5 h-3.5 flex-shrink-0" />
                    <span>{WORDING[state.key]}</span>
                </div>
                {regNo && (
                    <div className="font-mono text-[11px] mt-0.5 break-all opacity-90">
                        {kind === 'aerb' ? 'AERB' : 'PC-PNDT'} · {regNo}
                    </div>
                )}
            </div>
        </div>
    );
}

function ModalityChip({ modality }) {
    const { t } = useTranslation();
    const cfg = MODALITIES[modality] || MODALITIES.other;
    return (
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded border text-[11px] font-semibold ${cfg.chip}`}>
            <ScanLine className="w-3 h-3" />
            {t(cfg.labelKey, cfg.label)}
        </span>
    );
}

function StatusChip({ status }) {
    const { t } = useTranslation();
    const cfg = STATUSES[status] || STATUSES.active;
    const Icon = cfg.icon;
    return (
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded border text-[11px] font-semibold ${cfg.chip}`}>
            <Icon className="w-3 h-3" />
            {t(cfg.labelKey, cfg.label)}
        </span>
    );
}

/* ------------------------------------------------------------------ *
 * The screen
 * ------------------------------------------------------------------ */

export default function ImagingEquipment() {
    const { t } = useTranslation();
    const { can } = usePermissions();
    const axiosInstance = useMemo(() => createAuthedAxios(), []);

    const canManage = can('radiology.master.manage');

    const [fleet, setFleet] = useState([]);
    const [loading, setLoading] = useState(true);

    const [searchQuery, setSearchQuery] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [modalityFilter, setModalityFilter] = useState('');
    // Empty means every machine, retired ones included — they sort to the bottom
    // and are drawn dimmed rather than removed. A retired row is how somebody
    // discovers that the unit they are about to re-create already exists, with
    // years of studies attached to it; the same position the pharmacy takes on a
    // closed counter, for the same reason.
    const [statusFilter, setStatusFilter] = useState('');

    const [draft, setDraft] = useState(null);
    const [mutating, setMutating] = useState(false);
    const [refreshKey, setRefreshKey] = useState(0);

    const searchTimeoutRef = useRef(null);

    const refetch = useCallback(() => setRefreshKey((k) => k + 1), []);

    useEffect(() => {
        if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
        searchTimeoutRef.current = setTimeout(() => setDebouncedSearch(searchQuery.trim()), SEARCH_DEBOUNCE_MS);
        return () => { if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current); };
    }, [searchQuery]);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true);
            try {
                const res = await axiosInstance.get(API.list, {
                    params: {
                        search: debouncedSearch,
                        modality: modalityFilter,
                        status: statusFilter,
                        limit: FLEET_LIMIT,
                    },
                });
                if (cancelled) return;
                setFleet(res?.data?.data || []);
            } catch (err) {
                if (cancelled) return;
                setFleet([]);
                toast.warn(apiError(err, t('imagingequipment.load_error', 'Could not load the machines')));
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [debouncedSearch, modalityFilter, statusFilter, refreshKey]);

    // Retired last, then anything that stops work today, then anything expiring,
    // then by name. See the header: the point of the page is what is blocking the
    // department, and a blocked machine sorted eleventh is not an answer.
    const machines = useMemo(() => {
        const rank = (eq) => {
            if (eq.status === 'retired') return 3;
            if (blocksWork(eq)) return 0;
            if (aerbState(eq).level === 'expiring' || pndtState(eq).level === 'expiring') return 1;
            return 2;
        };
        return [...fleet].sort((a, b) =>
            rank(a) - rank(b) || String(a.name || '').localeCompare(String(b.name || '')));
    }, [fleet]);

    const blockedCount = useMemo(() => machines.filter(blocksWork).length, [machines]);
    const expiringCount = useMemo(() => machines.filter((eq) =>
        eq.status !== 'retired'
        && !blocksWork(eq)
        && (aerbState(eq).level === 'expiring' || pndtState(eq).level === 'expiring')).length, [machines]);

    const openCreate = () => setDraft({ ...EMPTY_EQUIPMENT });
    const openEdit = (eq) => setDraft({
        equipmentId: eq.equipmentId,
        name: eq.name || '',
        modality: MODALITIES[eq.modality] ? eq.modality : 'other',
        manufacturer: eq.manufacturer || '',
        model: eq.model || '',
        serialNo: eq.serialNo || '',
        room: eq.room || '',
        aerbRegNo: eq.aerbRegNo || '',
        aerbExpiry: (eq.aerbExpiry || '').slice(0, 10),
        pndtRegNo: eq.pndtRegNo || '',
        pndtExpiry: (eq.pndtExpiry || '').slice(0, 10),
        status: STATUSES[eq.status] ? eq.status : 'active',
        slotMinutes: eq.slotMinutes ?? 15,
        openTime: eq.openTime || '09:00',
        closeTime: eq.closeTime || '18:00',
    });

    /* ---------------------------------------------------------------- *
     * Writes
     * ---------------------------------------------------------------- */

    const save = async () => {
        const name = draft.name.trim();
        if (!name) {
            toast.warn(t('imagingequipment.name_required', 'Give the machine the name staff actually call it — that is what a porter is told'));
            return;
        }

        const slotMinutes = parseInt(draft.slotMinutes, 10);
        if (!Number.isFinite(slotMinutes) || slotMinutes <= 0) {
            toast.warn(t('imagingequipment.slot_required', 'Slot length must be at least a minute — it is the grid the day is laid out on'));
            return;
        }
        // A closing time at or before the opening time produces a working day with
        // no slots in it, and the machine then simply never appears in the
        // scheduler. That reads to the booking desk as a broken feature rather
        // than as a typo, so it is caught here where the typo was made.
        if (draft.closeTime <= draft.openTime) {
            toast.warn(t('imagingequipment.hours_invalid', 'The machine closes before it opens, so its day holds no slots at all'));
            return;
        }

        const payload = {
            name,
            modality: draft.modality,
            manufacturer: draft.manufacturer.trim() || null,
            model: draft.model.trim() || null,
            serialNo: draft.serialNo.trim() || null,
            room: draft.room.trim() || null,
            aerbRegNo: draft.aerbRegNo.trim() || null,
            // Empty means "no certificate recorded", which the gate reads as
            // unregistered. Sent as null rather than '' so the column says the
            // same thing the screen does.
            aerbExpiry: draft.aerbExpiry || null,
            pndtRegNo: draft.pndtRegNo.trim() || null,
            pndtExpiry: draft.pndtExpiry || null,
            status: draft.status,
            slotMinutes,
            openTime: draft.openTime,
            closeTime: draft.closeTime,
        };

        setMutating(true);
        try {
            if (draft.equipmentId) {
                await axiosInstance.put(API.one(draft.equipmentId), payload);
                toast.success(t('imagingequipment.updated', 'Machine updated'));
            } else {
                await axiosInstance.post(API.list, payload);
                toast.success(t('imagingequipment.created', 'Machine added'));
            }
            setDraft(null);
            refetch();
        } catch (err) {
            toast.error(apiError(err, t('imagingequipment.save_error', 'Failed to save the machine')));
        } finally {
            setMutating(false);
        }
    };

    /**
     * Move a machine between the three states.
     *
     * Retiring is confirmed and the others are not, because retiring is the one
     * that is meant to be permanent — the row survives for the inspection, but
     * bringing a sold machine back into a schedule is not something anybody should
     * do by mis-clicking a dropdown.
     */
    const setStatus = async (eq, status) => {
        if (status === 'retired') {
            const result = await MySwal.fire({
                title: t('imagingequipment.retire_title', 'Retire {{name}}?', { name: eq.name }),
                text: t('imagingequipment.retire_text', 'It stops being bookable. Nothing is deleted — every study it performed still names it, which is what an AERB or PC-PNDT inspection asks about.'),
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#4ABDBD',
                cancelButtonColor: '#e5e7eb',
                confirmButtonText: t('imagingequipment.retire', 'Retire'),
                cancelButtonText: t('imagingequipment.cancel', 'Cancel'),
            });
            if (!result.isConfirmed) return;
        }

        setMutating(true);
        try {
            // PUT rather than DELETE — status has three values and a verb has one.
            // See the header.
            const res = await axiosInstance.put(API.one(eq.equipmentId), { status });
            // The server refuses to retire or take down a machine with studies
            // still booked on it, and names them; the refusal is the useful half,
            // because those patients have to be moved somewhere before the room
            // goes dark.
            toast.success(res?.data?.message || t('imagingequipment.status_changed', 'Machine updated'));
            refetch();
        } catch (err) {
            toast.warn(apiError(err, t('imagingequipment.status_error', 'Failed to change the machine’s state')));
        } finally {
            setMutating(false);
        }
    };

    const inputCls = 'w-full px-3 py-2.5 text-sm border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#4ABDBD]';
    const labelCls = 'text-xs font-medium text-slate-500';

    const draftModality = MODALITIES[draft?.modality] || MODALITIES.other;

    return (
        <div data-testid="imaging-equipment-page-container" className="bg-white p-4 rounded-2xl shadow-md sm:p-6 w-full">
            <div className="flex flex-col sm:flex-row justify-between items-center mb-6 gap-4">
                <div className="ml-2">
                    <h1 data-testid="imaging-equipment-page-title" className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] bg-clip-text text-transparent">
                        {t('imagingequipment.title', 'Machines & registrations')}
                    </h1>
                    <p className="text-xs text-slate-400 mt-1">
                        {t('imagingequipment.subtitle', 'Every scanner the department owns, the room it stands in, and the licence that says it may be switched on.')}
                    </p>
                </div>
                <button
                    data-testid="add-equipment-button"
                    onClick={openCreate}
                    disabled={loading || !canManage}
                    title={canManage ? undefined : t('imagingequipment.no_permission', 'You do not have permission to manage machines (radiology.master.manage)')}
                    className={`group flex items-center gap-2 w-full sm:w-auto px-4 py-2 bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] text-white rounded-xl shadow-lg transition-all duration-300 font-semibold text-sm whitespace-nowrap ${loading || !canManage ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:shadow-xl hover:scale-105'}`}
                >
                    <Plus className="w-5 h-5" />
                    {t('imagingequipment.add', 'Add machine')}
                </button>
            </div>

            {/* THE HEADLINE. A refused booking with a patient at the counter is the
                alternative to this sentence being read here first. */}
            {!loading && blockedCount > 0 && (
                <div
                    data-testid="equipment-blocked-banner"
                    className="mb-4 flex items-start gap-3 p-3 rounded-xl bg-red-50 border-2 border-red-300"
                >
                    <ShieldX className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-red-800">
                        <span className="font-bold">
                            {t('imagingequipment.blocked_count', '{{n}} machine(s) cannot be booked or used today', { n: blockedCount })}
                        </span>
                        <p className="text-xs text-red-700 mt-0.5">
                            {t(
                                'imagingequipment.blocked_hint',
                                'A lapsed or missing registration is refused at booking and again at the console, by machine, with the reason named. Operating an unregistered unit gets it sealed and the person in charge prosecuted — the rooms below are listed first.'
                            )}
                        </p>
                    </div>
                </div>
            )}

            {!loading && expiringCount > 0 && (
                <div
                    data-testid="equipment-expiring-banner"
                    className="mb-4 flex items-start gap-3 p-3 rounded-xl bg-amber-50 border border-amber-300"
                >
                    <CalendarClock className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-amber-900">
                        <span className="font-bold">
                            {t('imagingequipment.expiring_count', '{{n}} registration(s) expire within {{days}} days', { n: expiringCount, days: EXPIRING_SOON_DAYS })}
                        </span>
                        <p className="text-xs text-amber-800 mt-0.5">
                            {t('imagingequipment.expiring_hint', 'Still lawful, and this is the only window in which a renewal can be chased without losing a list. A renewal takes weeks to come back.')}
                        </p>
                    </div>
                </div>
            )}

            <div className="mb-6 flex flex-col sm:flex-row gap-2 sm:items-center flex-wrap">
                <div className="relative w-full sm:w-80">
                    <input
                        data-testid="equipment-search-input"
                        type="text"
                        placeholder={t('imagingequipment.search_placeholder', 'Search name / room / serial / registration…')}
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value.replace(/[<>]/g, '').slice(0, 60))}
                        maxLength={60}
                        className="w-full p-2 pl-10 pr-8 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#4ABDBD]"
                    />
                    <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#4ABDBD]" />
                    {searchQuery && (
                        <button
                            type="button"
                            onClick={() => setSearchQuery('')}
                            className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 cursor-pointer"
                        >
                            <X className="w-4 h-4" />
                        </button>
                    )}
                </div>

                <select
                    data-testid="equipment-modality-filter"
                    value={modalityFilter}
                    onChange={(e) => setModalityFilter(e.target.value)}
                    className="tt-select w-full sm:w-44 p-2 border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#4ABDBD]"
                >
                    <option value="">{t('imagingequipment.all_modalities', 'All modalities')}</option>
                    {MODALITY_KEYS.map((m) => (
                        <option key={m} value={m}>{t(MODALITIES[m].labelKey, MODALITIES[m].label)}</option>
                    ))}
                </select>

                <select
                    data-testid="equipment-status-filter"
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="tt-select w-full sm:w-52 p-2 border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#4ABDBD]"
                >
                    <option value="">{t('imagingequipment.all_statuses', 'Every machine')}</option>
                    {Object.keys(STATUSES).map((s) => (
                        <option key={s} value={s}>{t(STATUSES[s].labelKey, STATUSES[s].label)}</option>
                    ))}
                </select>
            </div>

            {loading ? (
                <div className="flex flex-col items-center justify-center py-4 pt-12">
                    <CommonLoader />
                    <span className="text-xl text-[#4ABDBD]">{t('imagingequipment.loading', 'Loading...')}</span>
                </div>
            ) : machines.length === 0 ? (
                <div className="p-8 text-center rounded-2xl border border-dashed border-gray-300">
                    <Cpu className="w-8 h-8 mx-auto text-slate-300 mb-2" />
                    <p className="text-sm text-gray-500">
                        {t('imagingequipment.empty', 'No machine recorded yet. A radiology department’s capacity is its machines — until one exists here, nothing can be scheduled.')}
                    </p>
                </div>
            ) : (
                <div data-testid="equipment-grid" className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
                    {machines.map((eq) => {
                        const aerb = aerbState(eq);
                        const pndt = pndtState(eq);
                        const blocked = blocksWork(eq);
                        const retired = eq.status === 'retired';
                        return (
                            <div
                                key={eq.equipmentId}
                                data-testid={`equipment-card-${eq.equipmentId}`}
                                data-blocked={blocked ? 'true' : 'false'}
                                // The card's own border is the fourth coding of the same
                                // fact the rows below state in words — red for a machine
                                // that cannot be used, and nothing louder than a plain
                                // border for one that can.
                                className={`rounded-2xl border-2 overflow-hidden transition-all duration-300 ${blocked
                                    ? 'border-red-400 bg-red-50/40 ring-1 ring-red-200'
                                    : 'border-gray-200 bg-white hover:border-[#4ABDBD]/60 hover:shadow'} ${retired ? 'opacity-60' : ''}`}
                            >
                                <div className="p-4">
                                    <div className="flex items-start justify-between gap-2">
                                        <div className="flex items-start gap-2 min-w-0">
                                            <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${blocked ? 'bg-red-100 text-red-600' : 'bg-[#4ABDBD]/10 text-[#2D8A8A]'}`}>
                                                <Cpu className="w-4 h-4" />
                                            </div>
                                            <div className="min-w-0">
                                                <div className="font-bold text-gray-800 text-sm truncate">{eq.name}</div>
                                                <div className="text-[11px] text-slate-400 truncate">
                                                    {[eq.manufacturer, eq.model].filter(Boolean).join(' ')}
                                                </div>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-1 flex-shrink-0">
                                            <button
                                                data-testid={`edit-equipment-${eq.equipmentId}`}
                                                onClick={() => openEdit(eq)}
                                                disabled={!canManage}
                                                title={canManage ? t('imagingequipment.edit', 'Edit') : t('imagingequipment.no_permission', 'You do not have permission to manage machines (radiology.master.manage)')}
                                                className={`p-1.5 rounded-lg text-[#4ABDBD] bg-blue-50 ${canManage ? 'hover:bg-[#4ABDBD] hover:text-white cursor-pointer' : 'opacity-40 cursor-not-allowed'}`}
                                            >
                                                <Pencil className="w-3.5 h-3.5" />
                                            </button>
                                            {!retired ? (
                                                <>
                                                    {eq.status === 'active' ? (
                                                        <button
                                                            data-testid={`maintenance-equipment-${eq.equipmentId}`}
                                                            onClick={() => setStatus(eq, 'maintenance')}
                                                            disabled={!canManage || mutating}
                                                            title={t('imagingequipment.to_maintenance', 'Take out of the schedule for maintenance')}
                                                            className={`p-1.5 rounded-lg text-amber-700 bg-amber-50 ${canManage && !mutating ? 'hover:bg-amber-500 hover:text-white cursor-pointer' : 'opacity-40 cursor-not-allowed'}`}
                                                        >
                                                            <Wrench className="w-3.5 h-3.5" />
                                                        </button>
                                                    ) : (
                                                        <button
                                                            data-testid={`activate-equipment-${eq.equipmentId}`}
                                                            onClick={() => setStatus(eq, 'active')}
                                                            disabled={!canManage || mutating}
                                                            title={t('imagingequipment.back_in_service', 'Back in service')}
                                                            className={`p-1.5 rounded-lg text-emerald-600 bg-emerald-50 ${canManage && !mutating ? 'hover:bg-emerald-500 hover:text-white cursor-pointer' : 'opacity-40 cursor-not-allowed'}`}
                                                        >
                                                            <RotateCcw className="w-3.5 h-3.5" />
                                                        </button>
                                                    )}
                                                    <button
                                                        data-testid={`retire-equipment-${eq.equipmentId}`}
                                                        onClick={() => setStatus(eq, 'retired')}
                                                        disabled={!canManage || mutating}
                                                        title={t('imagingequipment.retire', 'Retire')}
                                                        className={`p-1.5 rounded-lg text-slate-500 bg-slate-100 ${canManage && !mutating ? 'hover:bg-slate-600 hover:text-white cursor-pointer' : 'opacity-40 cursor-not-allowed'}`}
                                                    >
                                                        <Archive className="w-3.5 h-3.5" />
                                                    </button>
                                                </>
                                            ) : (
                                                <button
                                                    data-testid={`unretire-equipment-${eq.equipmentId}`}
                                                    onClick={() => setStatus(eq, 'active')}
                                                    disabled={!canManage || mutating}
                                                    title={t('imagingequipment.back_in_service', 'Back in service')}
                                                    className={`p-1.5 rounded-lg text-emerald-600 bg-emerald-50 ${canManage && !mutating ? 'hover:bg-emerald-500 hover:text-white cursor-pointer' : 'opacity-40 cursor-not-allowed'}`}
                                                >
                                                    <RotateCcw className="w-3.5 h-3.5" />
                                                </button>
                                            )}
                                        </div>
                                    </div>

                                    <div className="flex flex-wrap items-center gap-2 mt-3">
                                        <ModalityChip modality={eq.modality} />
                                        <StatusChip status={eq.status} />
                                        {eq.room && (
                                            <span className="inline-flex items-center gap-1 text-[11px] text-slate-600">
                                                <MapPin className="w-3 h-3 text-[#4ABDBD]" />
                                                {eq.room}
                                            </span>
                                        )}
                                    </div>

                                    {/* The serial is what an inspector matches against the
                                        AERB certificate in the file — it is the only thing
                                        separating two identical units bought together. */}
                                    {eq.serialNo && (
                                        <div className="text-[11px] text-slate-400 font-mono mt-2 break-all">
                                            {t('imagingequipment.serial', 'Serial')} · {eq.serialNo}
                                        </div>
                                    )}

                                    {/* ---- the licences ---- */}
                                    <div className="mt-3 space-y-2">
                                        <RegistrationRow
                                            kind="aerb"
                                            state={aerb}
                                            regNo={aerb.regNo}
                                            expiry={eq.aerbExpiry}
                                            testId={`aerb-${eq.equipmentId}`}
                                        />
                                        <RegistrationRow
                                            kind="pndt"
                                            state={pndt}
                                            regNo={pndt.regNo}
                                            expiry={eq.pndtExpiry}
                                            testId={`pndt-${eq.equipmentId}`}
                                        />
                                    </div>

                                    {/* The working day, which is what the slot grid is
                                        actually built from. A machine whose hours are wrong
                                        is a machine that silently offers no appointments. */}
                                    <div className="flex flex-wrap items-center gap-3 mt-3 pt-3 border-t border-black/5 text-[11px] text-slate-500">
                                        <span className="inline-flex items-center gap-1">
                                            <Clock className="w-3 h-3 text-[#4ABDBD]" />
                                            <span data-testid={`equipment-hours-${eq.equipmentId}`}>
                                                {formatClock(eq.openTime) || '—'}–{formatClock(eq.closeTime) || '—'}
                                            </span>
                                        </span>
                                        <span>
                                            {t('imagingequipment.slot_every', 'slot every {{n}} min', { n: eq.slotMinutes ?? 15 })}
                                        </span>
                                        {retired && (
                                            <span className="ml-auto font-bold uppercase text-slate-400">
                                                {t('imagingequipment.retired_note', 'kept for the record')}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* ---- the form ---- */}
            {draft && (
                <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40 p-4" data-testid="equipment-form-modal">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
                        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
                            <h2 className="text-lg font-bold text-[#1a365d]">
                                {draft.equipmentId
                                    ? t('imagingequipment.edit_title', 'Edit machine')
                                    : t('imagingequipment.add', 'Add machine')}
                            </h2>
                            <button type="button" onClick={() => setDraft(null)} data-testid="equipment-form-close" className="text-gray-400 hover:text-gray-600 cursor-pointer">
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        <div className="p-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div>
                                <label className={labelCls}>{t('imagingequipment.name', 'Name')} *</label>
                                <input
                                    data-testid="equipment-form-name"
                                    value={draft.name}
                                    onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value.slice(0, 80) }))}
                                    placeholder={t('imagingequipment.name_placeholder', 'e.g. CT-1, Portable X-Ray (Ward), USG Room 2')}
                                    maxLength={80}
                                    className={inputCls}
                                />
                                <p className="text-[11px] text-slate-400 mt-1">
                                    {t('imagingequipment.name_hint', 'What staff say out loud — a booking clerk picks it and a porter is told it. Not the manufacturer’s model number.')}
                                </p>
                            </div>
                            <div>
                                <label className={labelCls}>{t('imagingequipment.modality', 'Modality')} *</label>
                                <select
                                    data-testid="equipment-form-modality"
                                    value={draft.modality}
                                    onChange={(e) => setDraft((d) => ({ ...d, modality: e.target.value }))}
                                    className={`tt-select ${inputCls}`}
                                >
                                    {MODALITY_KEYS.map((m) => (
                                        <option key={m} value={m}>{t(MODALITIES[m].labelKey, MODALITIES[m].label)}</option>
                                    ))}
                                </select>
                                <p className="text-[11px] text-slate-400 mt-1">
                                    {t('imagingequipment.modality_hint', 'What this machine can perform. Matched against the procedure’s modality when a scan is booked.')}
                                </p>
                            </div>

                            <div>
                                <label className={labelCls}>{t('imagingequipment.manufacturer', 'Manufacturer')}</label>
                                <input
                                    data-testid="equipment-form-manufacturer"
                                    value={draft.manufacturer}
                                    onChange={(e) => setDraft((d) => ({ ...d, manufacturer: e.target.value.slice(0, 60) }))}
                                    placeholder="Siemens"
                                    maxLength={60}
                                    className={inputCls}
                                />
                            </div>
                            <div>
                                <label className={labelCls}>{t('imagingequipment.model', 'Model')}</label>
                                <input
                                    data-testid="equipment-form-model"
                                    value={draft.model}
                                    onChange={(e) => setDraft((d) => ({ ...d, model: e.target.value.slice(0, 60) }))}
                                    placeholder="Somatom go.Now"
                                    maxLength={60}
                                    className={inputCls}
                                />
                            </div>

                            <div>
                                <label className={labelCls}>{t('imagingequipment.serial', 'Serial number')}</label>
                                <input
                                    data-testid="equipment-form-serial"
                                    value={draft.serialNo}
                                    onChange={(e) => setDraft((d) => ({ ...d, serialNo: e.target.value.slice(0, 60) }))}
                                    maxLength={60}
                                    className={`${inputCls} font-mono`}
                                />
                                {/* Deliberately not enforced unique: a hospital adding a
                                    machine before the engineer has read the plate off the
                                    back should be able to save the row, and a
                                    wrong-but-unique serial is worse than a blank one. */}
                                <p className="text-[11px] text-slate-400 mt-1">
                                    {t('imagingequipment.serial_hint', 'The number printed on the AERB certificate. It is how an inspector matches the paper in the file to the tube in the room.')}
                                </p>
                            </div>
                            <div>
                                <label className={labelCls}>{t('imagingequipment.room', 'Room')}</label>
                                <input
                                    data-testid="equipment-form-room"
                                    value={draft.room}
                                    onChange={(e) => setDraft((d) => ({ ...d, room: e.target.value.slice(0, 60) }))}
                                    maxLength={60}
                                    className={inputCls}
                                />
                                {/* An AERB registration is for this unit IN THIS ROOM: the
                                    radiation survey and the lead shielding certificate apply
                                    to the room, so moving a machine is a regulatory event
                                    rather than furniture rearrangement. */}
                                <p className="text-[11px] text-slate-400 mt-1">
                                    {t('imagingequipment.room_hint', 'Where the patient is sent — and where the shielding certificate applies. Moving a machine between rooms is a regulatory event.')}
                                </p>
                            </div>

                            {/* ---- AERB ---- */}
                            <div className={`sm:col-span-2 rounded-xl border-2 p-3 ${draftModality.ionising ? 'border-amber-300 bg-amber-50/50' : 'border-slate-200 bg-slate-50/60'}`}>
                                <div className="flex items-center gap-2 mb-2">
                                    <Radiation className={`w-4 h-4 ${draftModality.ionising ? 'text-amber-700' : 'text-slate-400'}`} />
                                    <span className="text-sm font-bold text-gray-800">
                                        {t('imagingequipment.aerb_heading', 'AERB registration')}
                                    </span>
                                    {draftModality.ionising && (
                                        <span className="px-1.5 py-0.5 rounded bg-amber-600 text-white text-[10px] font-black uppercase tracking-wide">
                                            {t('imagingequipment.required', 'Required')}
                                        </span>
                                    )}
                                </div>
                                <p className="text-[11px] text-slate-600 mb-3">
                                    {draftModality.ionising
                                        ? t('imagingequipment.aerb_required_hint', 'This modality emits ionising radiation and is registered as an individual unit. Without a current number AND expiry, booking and performing are both refused on this machine — the hospital does not hold one licence covering the department.')
                                        : t('imagingequipment.aerb_na_hint', 'AERB does not register this modality. Leave both fields blank — a number entered here will be enforced whatever the modality, which is how a C-arm filed as “other” is still caught.')}
                                </p>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                    <div>
                                        <label className={labelCls}>{t('imagingequipment.aerb_no', 'Registration number')}</label>
                                        <input
                                            data-testid="equipment-form-aerb-no"
                                            value={draft.aerbRegNo}
                                            onChange={(e) => setDraft((d) => ({ ...d, aerbRegNo: e.target.value.slice(0, 60) }))}
                                            maxLength={60}
                                            className={`${inputCls} font-mono`}
                                        />
                                    </div>
                                    <div>
                                        <label className={labelCls}>{t('imagingequipment.aerb_expiry', 'Valid through')}</label>
                                        <input
                                            data-testid="equipment-form-aerb-expiry"
                                            type="date"
                                            value={draft.aerbExpiry}
                                            onChange={(e) => setDraft((d) => ({ ...d, aerbExpiry: e.target.value }))}
                                            className={inputCls}
                                        />
                                    </div>
                                </div>
                                {/* A number with no date is the shape that looks filled in
                                    and is treated as unregistered — worth saying at the
                                    moment it is created rather than when a booking fails. */}
                                {draft.aerbRegNo.trim() && !draft.aerbExpiry && (
                                    <p data-testid="equipment-form-aerb-warning" className="text-[11px] text-red-700 font-semibold mt-2 flex items-start gap-1">
                                        <AlertTriangle className="w-3 h-3 mt-0.5 flex-shrink-0" />
                                        {t('imagingequipment.aerb_no_expiry_warning', 'A registration with no expiry is treated as unregistered — this machine will be refused until a date is recorded.')}
                                    </p>
                                )}
                                {draft.aerbExpiry && daysUntil(draft.aerbExpiry) !== null && daysUntil(draft.aerbExpiry) < 0 && (
                                    <p className="text-[11px] text-red-700 font-semibold mt-2 flex items-start gap-1">
                                        <AlertTriangle className="w-3 h-3 mt-0.5 flex-shrink-0" />
                                        {t('imagingequipment.aerb_past_warning', 'That date has already passed. Saving it is fine — it is the truth — but nothing can be booked on this machine until it is renewed.')}
                                    </p>
                                )}
                            </div>

                            {/* ---- PC-PNDT ---- */}
                            <div className={`sm:col-span-2 rounded-xl border-2 p-3 ${draftModality.foetal ? 'border-red-300 bg-red-50/50' : 'border-slate-200 bg-slate-50/60'}`}>
                                <div className="flex items-center gap-2 mb-2">
                                    <Baby className={`w-4 h-4 ${draftModality.foetal ? 'text-red-600' : 'text-slate-400'}`} />
                                    <span className="text-sm font-bold text-gray-800">
                                        {t('imagingequipment.pndt_heading', 'PC-PNDT registration')}
                                    </span>
                                </div>
                                <p className="text-[11px] text-slate-600 mb-3">
                                    {draftModality.foetal
                                        ? t('imagingequipment.pndt_usg_hint', 'Required only to perform scans declared within the Act — an abdominal scan on an unregistered console is lawful, an obstetric one is not. The registration is granted to this machine at this registered address; move it to another branch and it does not travel with it.')
                                        : t('imagingequipment.pndt_na_hint', 'PC-PNDT registers ultrasound machines capable of foetal imaging. Leave both blank unless this machine holds a certificate.')}
                                </p>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                    <div>
                                        <label className={labelCls}>{t('imagingequipment.pndt_no', 'Registration number')}</label>
                                        <input
                                            data-testid="equipment-form-pndt-no"
                                            value={draft.pndtRegNo}
                                            onChange={(e) => setDraft((d) => ({ ...d, pndtRegNo: e.target.value.slice(0, 60) }))}
                                            maxLength={60}
                                            className={`${inputCls} font-mono`}
                                        />
                                    </div>
                                    <div>
                                        <label className={labelCls}>{t('imagingequipment.pndt_expiry', 'Valid through')}</label>
                                        <input
                                            data-testid="equipment-form-pndt-expiry"
                                            type="date"
                                            value={draft.pndtExpiry}
                                            onChange={(e) => setDraft((d) => ({ ...d, pndtExpiry: e.target.value }))}
                                            className={inputCls}
                                        />
                                    </div>
                                </div>
                                {draft.pndtRegNo.trim() && !draft.pndtExpiry && (
                                    <p data-testid="equipment-form-pndt-warning" className="text-[11px] text-red-700 font-semibold mt-2 flex items-start gap-1">
                                        <AlertTriangle className="w-3 h-3 mt-0.5 flex-shrink-0" />
                                        {t('imagingequipment.pndt_no_expiry_warning', 'A registration with no expiry is treated as unregistered — no scan inside the Act may be performed on this machine until a date is recorded.')}
                                    </p>
                                )}
                            </div>

                            {/* ---- the working day ---- (the two clock fields take
                                their own width: hour / minute / AM-PM selects) */}
                            <div className="sm:col-span-2 grid grid-cols-1 sm:grid-cols-[auto_auto_minmax(0,1fr)] gap-3">
                                <div>
                                    <label className={labelCls}>{t('imagingequipment.open_time', 'Opens')}</label>
                                    <div className="mt-0.5">
                                        <TimeSelect
                                            testId="equipment-form-open"
                                            value={draft.openTime}
                                            onChange={(openTime) => setDraft((d) => ({ ...d, openTime }))}
                                            ariaLabel={t('imagingequipment.open_time', 'Opens')}
                                        />
                                    </div>
                                </div>
                                <div>
                                    <label className={labelCls}>{t('imagingequipment.close_time', 'Closes')}</label>
                                    <div className="mt-0.5">
                                        <TimeSelect
                                            testId="equipment-form-close"
                                            value={draft.closeTime}
                                            onChange={(closeTime) => setDraft((d) => ({ ...d, closeTime }))}
                                            ariaLabel={t('imagingequipment.close_time', 'Closes')}
                                        />
                                    </div>
                                </div>
                                <div>
                                    <label className={labelCls}>{t('imagingequipment.slot_minutes', 'Slot length (min)')}</label>
                                    <input
                                        data-testid="equipment-form-slot"
                                        type="number"
                                        min="1"
                                        step="1"
                                        value={draft.slotMinutes}
                                        onChange={(e) => setDraft((d) => ({ ...d, slotMinutes: e.target.value }))}
                                        className={inputCls}
                                    />
                                </div>
                                {/* Per machine and not per department: a busy X-ray room
                                    turns a patient round every ten minutes while an MRI is
                                    booked in half-hours, and one shared grid either offers
                                    MRI start times that cannot exist or hides two-thirds of
                                    the X-ray room's usable day. The times are local wall
                                    clock — '09:00' means nine in the morning where the
                                    machine stands, on every date. */}
                                <p className="sm:col-span-3 text-[11px] text-slate-400">
                                    {t('imagingequipment.hours_hint', 'The grid this machine’s day is laid out on — its own, because an X-ray room turns a patient round in ten minutes and an MRI is booked in half-hours. Local clock time at the machine.')}
                                </p>
                            </div>

                            <div className="sm:col-span-2">
                                <label className={labelCls}>{t('imagingequipment.status', 'State')}</label>
                                <select
                                    data-testid="equipment-form-status"
                                    value={draft.status}
                                    onChange={(e) => setDraft((d) => ({ ...d, status: e.target.value }))}
                                    className={`tt-select ${inputCls}`}
                                >
                                    {Object.keys(STATUSES).map((s) => (
                                        <option key={s} value={s}>{t(STATUSES[s].labelKey, STATUSES[s].label)}</option>
                                    ))}
                                </select>
                                <p className="text-[11px] text-slate-400 mt-1">
                                    {t('imagingequipment.status_hint', 'Maintenance is its own state, not a flavour of retired — a scanner down for a tube change comes back next week, and the day it does nobody should have to remember which it was.')}
                                </p>
                            </div>
                        </div>

                        <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-gray-100">
                            <button
                                type="button"
                                onClick={() => setDraft(null)}
                                className="px-4 py-2 text-sm rounded-xl border border-gray-300 text-gray-600 hover:bg-gray-50 cursor-pointer"
                            >
                                {t('imagingequipment.cancel', 'Cancel')}
                            </button>
                            <button
                                type="button"
                                onClick={save}
                                disabled={mutating}
                                data-testid="equipment-form-submit"
                                className="px-4 py-2 text-sm rounded-xl text-white font-semibold bg-gradient-to-r from-[#4ABDBD] to-[#2D8A8A] hover:shadow-lg disabled:opacity-60 flex items-center gap-2 cursor-pointer"
                            >
                                {mutating && <Loader2 className="w-4 h-4 animate-spin" />}
                                {draft.equipmentId
                                    ? t('imagingequipment.save', 'Save changes')
                                    : t('imagingequipment.create', 'Add machine')}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
